import React from 'react';
import {
  Users,
  UserCheck,
  BarChart3,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

/**
 * Composant de carte statistique Premium
 */
const StatCard = ({ title, value, label, icon: Icon, trend, color, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay }}
      className={`relative overflow-hidden rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700/50 backdrop-blur-xl ${color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/40 dark:to-slate-900/80' :
          color === 'emerald' ? 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900/80' :
            color === 'purple' ? 'bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/40 dark:to-slate-900/80' :
              'bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/40 dark:to-slate-900/80'
        }`}
    >
      <div className={`absolute -right-6 -top-6 h-32 w-32 rounded-full opacity-10 blur-3xl ${color === 'blue' ? 'bg-blue-400' :
          color === 'emerald' ? 'bg-emerald-400' :
            color === 'purple' ? 'bg-purple-400' :
              'bg-amber-400'
        }`} />

      <div className="relative z-10 flex justify-between items-start">
        <div>
          <p className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{value}</h3>

          {trend && (
            <div className={`flex items-center text-xs font-semibold ${trend > 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
              {trend > 0 ? <ArrowUpRight size={14} className="mr-1" /> : <ArrowDownRight size={14} className="mr-1" />}
              <span>{Math.abs(trend)}%</span>
              <span className="text-gray-400 dark:text-slate-500 ml-1 font-normal">vs mois dernier</span>
            </div>
          )}

          {label && (
            <p className="text-gray-400 dark:text-slate-500 text-xs mt-2">{label}</p>
          )}
        </div>

        <div className={`p-3 rounded-xl ${color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
            color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
              color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                'bg-amber-500/20 text-amber-400'
          }`}>
          <Icon size={24} />
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Composant Tableau de bord Premium
 */
const TableauDeBordModule = ({ stats, employees, clients, appointments }) => {
  const isDark = document.documentElement.classList.contains('dark');

  // Données simulées pour le graphique (à adapter avec des vraies données si dispo)
  const chartData = [
    { name: 'Sem 1', interventions: Math.floor(stats.totalAppointments * 0.2) || 12, heures: Math.floor(stats.totalHoursThisMonth * 0.2) || 45 },
    { name: 'Sem 2', interventions: Math.floor(stats.totalAppointments * 0.25) || 15, heures: Math.floor(stats.totalHoursThisMonth * 0.25) || 52 },
    { name: 'Sem 3', interventions: Math.floor(stats.totalAppointments * 0.22) || 14, heures: Math.floor(stats.totalHoursThisMonth * 0.22) || 48 },
    { name: 'Sem 4', interventions: Math.floor(stats.totalAppointments * 0.33) || 18, heures: Math.floor(stats.totalHoursThisMonth * 0.33) || 60 },
  ];

  // Taux d'occupation (déjà dans stats ou calculé)
  // On utilise une valeur par défaut de 87% si stats.occupancyRate n'existe pas, ou on le calcule
  const occupationRate = stats.totalContracts > 0 ? Math.round((stats.activeContracts / stats.totalContracts) * 100) : 0;

  const occupationData = [
    { name: 'Occupé', value: occupationRate },
    { name: 'Disponible', value: 100 - occupationRate },
  ];
  const COLORS = ['#3b82f6', '#1e293b'];

  // Préparer les activités récentes
  const recentActivities = employees
    .filter(emp => emp.isActive !== false && !emp.isArchived)
    .map(employee => {
      const now = new Date();
      // Trouver les rendez-vous de cet intervenant
      const employeeAppointments = appointments
        .filter(apt => apt.employeeId === employee.id && apt.date && apt.startTime)
        .map(apt => {
          const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
          const [startHours, startMinutes] = apt.startTime.split(':').map(Number);
          const startDateTime = new Date(aptDate);
          startDateTime.setHours(startHours, startMinutes, 0, 0);

          return { ...apt, startDateTime };
        })
        .sort((a, b) => b.startDateTime - a.startDateTime);

      const lastAppointment = employeeAppointments[0];
      const client = lastAppointment ? clients.find(c => c.id === lastAppointment.clientId) : null;

      return lastAppointment ? {
        id: lastAppointment.id,
        type: 'intervention',
        time: lastAppointment.startDateTime,
        employeeName: `${employee.firstName} ${employee.lastName}`,
        clientName: client ? `${client.firstName} ${client.lastName}` : 'Inconnu',
        description: lastAppointment.description || 'Intervention programmée'
      } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.time - a.time)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 p-6 space-y-8 font-sans">

      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Tableau de Bord Ressources
        </h1>
        <p className="text-gray-500 dark:text-slate-400 mt-1">Vue d'ensemble et pilotage de l'activité</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Intervenants"
          value={stats.totalEmployees}
          label={`${stats.activeEmployees} actifs`}
          icon={Users}
          color="blue"
          delay={0.1}
        />
        <StatCard
          title="Bénéficiaires"
          value={stats.totalClients}
          label={`${stats.activeClients} actifs`}
          icon={UserCheck}
          color="emerald"
          delay={0.2}
        />
        <StatCard
          title="Interventions"
          value={stats.totalAppointments}
          label={`${stats.totalHoursThisMonth}h ce mois`}
          icon={Calendar}
          color="purple"
          delay={0.3}
        />
        <StatCard
          title="Contrats Actifs"
          value={stats.activeContracts}
          label={`${stats.expiringContracts > 0 ? `${stats.expiringContracts} à renouveler` : 'Aucun à renouveler'}`}
          icon={Activity}
          color="amber"
          delay={0.4}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left Column (2/3) */}
        <div className="lg:col-span-2 space-y-8">

          {/* Chart Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-white dark:bg-slate-800/50 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-2xl p-6 shadow-xl"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="text-blue-400" size={20} />
                Volume d'Activité
              </h3>
            </div>

            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorInterventions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorHeures" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" axisLine={false} tickLine={false} />
                  <YAxis stroke="#64748b" axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      borderColor: isDark ? '#334155' : '#e5e7eb',
                      borderRadius: '8px',
                      color: isDark ? '#f1f5f9' : '#1f2937',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                    itemStyle={{ color: isDark ? '#e2e8f0' : '#374151' }}
                  />
                  <Area type="monotone" dataKey="interventions" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorInterventions)" name="Interventions" />
                  <Area type="monotone" dataKey="heures" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorHeures)" name="Heures" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Upcoming Appointments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white dark:bg-slate-800/50 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-2xl p-6 shadow-xl"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="text-emerald-400" size={20} />
                Activités Récentes
              </h3>
            </div>

            <div className="space-y-4">
              {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-900/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800/80 transition-all border border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-600 group">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-slate-800 flex items-center justify-center text-gray-500 dark:text-slate-400 group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-colors">
                      <Activity size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{activity.employeeName}</h4>
                      <p className="text-sm text-gray-500 dark:text-slate-400 flex items-center gap-2">
                        <span className="text-gray-700 dark:text-slate-300">chez {activity.clientName}</span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      {activity.time.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">
                      {activity.time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-400 dark:text-slate-500">Aucune activité récente</div>
              )}
            </div>
          </motion.div>

        </div>

        {/* Right Column (1/3) */}
        <div className="space-y-8">

          {/* Alerts Panel (Contracts) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className={`backdrop-blur-md rounded-2xl p-6 shadow-xl relative overflow-hidden ${
              stats.expiringContracts > 0
                ? 'bg-gradient-to-b from-rose-50 to-white dark:from-rose-900/20 dark:to-slate-800/50 border border-rose-100 dark:border-rose-900/30'
                : 'bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800/50 border border-emerald-100 dark:border-emerald-900/30'
            }`}
          >
            <div className={`absolute top-0 right-0 w-24 h-24 blur-3xl rounded-full ${stats.expiringContracts > 0 ? 'bg-rose-500/10' : 'bg-emerald-500/10'}`}></div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4 relative z-10">
              {stats.expiringContracts > 0 ? (
                <AlertCircle className="text-rose-400" size={20} />
              ) : (
                <CheckCircle className="text-emerald-400" size={20} />
              )}
              {stats.expiringContracts > 0 ? 'Attention Requise' : 'Tout est à jour'}
            </h3>

            <div className="space-y-3 relative z-10">
              {stats.expiringContracts > 0 && (
                <div className="p-3 bg-gray-100 dark:bg-slate-900/80 rounded-lg border-l-4 border-rose-500 flex justify-between items-center">
                  <span className="text-rose-800 dark:text-rose-100 font-medium">Contrats à renouveler</span>
                  <span className="bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{stats.expiringContracts}</span>
                </div>
              )}
              {stats.expiringContracts === 0 && (
                <div className="text-center py-4 text-emerald-400 text-sm">
                  Aucun contrat critique
                </div>
              )}
            </div>
          </motion.div>

          {/* Activity Timeline / Occupation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
            className="bg-white dark:bg-slate-800/50 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-2xl p-6 shadow-xl flex flex-col items-center justify-center text-center"
          >
            <h4 className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-4">Contrats actifs (%)</h4>
            <div className="h-40 w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={occupationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {occupationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Center Text */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white block">{occupationRate}%</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">Basé sur les contrats actifs</p>
          </motion.div>

          {/* Quick Actions / New Intervenants */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white dark:bg-slate-800/50 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-2xl p-6 shadow-xl"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
              <TrendingUp className="text-purple-400" size={20} />
              Derniers Ajouts
            </h3>

            <div className="space-y-4">
              {employees.slice(0, 3).map((emp) => (
                <div key={emp.id} className="flex items-center gap-3">
                  {emp.photoURL ? (
                    <img
                      src={emp.photoURL}
                      alt={`${emp.firstName} ${emp.lastName}`}
                      className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: emp.color || '#6366f1' }}
                    >
                      {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-800 dark:text-slate-200">{emp.firstName} {emp.lastName}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500 capitalize">{emp.role || 'Intervenant'}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default TableauDeBordModule;
